import {
	type BeginUploadBody,
	type BeginUploadResponse,
	FILE_MAX_BYTES,
	type FileKind,
	type FileUrlResponse,
	type SupportingDocType,
	contentTypeAllowedFor,
} from "@seagames/contracts";
import { ApiError, api } from "../api/client";

/**
 * The file upload client (M4, PLAN §3.2). Three calls, no PENDING rows:
 *
 *   1. `POST /files/uploads`  → a presigned PUT + a signed upload token
 *   2. XHR `PUT` of the bytes straight to the bucket (progress events)
 *   3. `POST /files/complete` → the server verifies, re-encodes and records it
 *
 * The bytes never pass through the API. Public surface (S6 imports it):
 *
 *   uploadFile({ kind, acCardId, file, docType?, originalName?, onProgress?, signal? })
 *     → Promise<UploadedFile>                     throws ApiError (never a credential verdict)
 *   checkUploadable(kind, file) → UploadProblem | null   size / type, before any request
 *   deleteFile(fileId)                                   DELETE /files/:id
 *   fetchFileUrl(fileId) → FileUrlResponse               GET /files/:id/url (fresh presigned URLs)
 *
 * After a successful upload or delete, invalidate the record
 * (`invalidateAccreditations()` from `lib/api/accreditations`) — the file
 * appears in `record.files` and the pointers move server-side.
 */

export interface UploadInput {
	kind: FileKind;
	/** The card the upload is made from. For an ID copy it names the card through which the person is reached. */
	acCardId: string;
	/** A File from an input, or a Blob (a crop, a camera frame). Its `type` must be one the kind accepts. */
	file: Blob;
	/** Required for SUPPORTING_DOC, absent otherwise. */
	docType?: SupportingDocType | null;
	/** Kept (sanitised) as a document's download name. Defaults to `file.name` when `file` is a File. */
	originalName?: string | null;
	/** 0…1 while the bytes go up; 1 once they are in the bucket (the server then still processes them). */
	onProgress?: (fraction: number) => void;
	signal?: AbortSignal;
}

/** What `POST /files/complete` answers. Only `file_id` is relied on; the rest arrives with the record. */
export interface UploadedFile {
	file_id: string;
	[key: string]: unknown;
}

/** Why a file is refused before anything is sent. Translate with `files:problem.<code>`. */
export type UploadProblem = { code: "type" } | { code: "size"; maxMb: number };

/** Client-side pre-check (the server checks again and is the authority). */
export function checkUploadable(kind: FileKind, file: Blob): UploadProblem | null {
	if (!contentTypeAllowedFor(kind, file.type)) return { code: "type" };
	if (file.size > FILE_MAX_BYTES) return { code: "size", maxMb: Math.floor(FILE_MAX_BYTES / (1024 * 1024)) };
	return null;
}

export async function uploadFile(input: UploadInput): Promise<UploadedFile> {
	const { kind, acCardId, file, docType, signal, onProgress } = input;
	const name = input.originalName ?? (file instanceof File ? file.name : null);
	const body: BeginUploadBody = {
		kind,
		ac_card_id: acCardId,
		doc_type: kind === "SUPPORTING_DOC" ? (docType ?? null) : null,
		content_type: file.type,
		bytes: file.size,
		original_name: name ? name.slice(0, 255) : null,
	};
	const begun = await api.post<BeginUploadResponse>("/files/uploads", body, { signal });
	onProgress?.(0);
	await putPresigned(begun, file, onProgress, signal);
	return api.post<UploadedFile>("/files/complete", { upload_token: begun.upload_token }, { signal });
}

export const deleteFile = (fileId: string) => api.delete<unknown>(`/files/${fileId}`);

export const fetchFileUrl = (fileId: string, signal?: AbortSignal) => api.get<FileUrlResponse>(`/files/${fileId}/url`, { signal });

/**
 * The PUT to the presigned URL. XHR, not fetch: fetch has no upload progress.
 * The signed headers (`content-type`; the length comes from the Blob) are sent
 * exactly as given. A bucket refusal is `UPLOAD_INCOMPLETE` (start again);
 * no answer at all is `SERVICE_UNAVAILABLE`. Neither touches the session.
 * Exported for the other presigned uploads (merch product photos).
 */
export function putPresigned(
	begun: { upload_url: string; upload_headers: Readonly<Record<string, string>> },
	file: Blob,
	onProgress: UploadInput["onProgress"],
	signal?: AbortSignal,
): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
		const xhr = new XMLHttpRequest();
		xhr.open("PUT", begun.upload_url);
		for (const [k, v] of Object.entries(begun.upload_headers)) xhr.setRequestHeader(k, v);
		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable && e.total > 0) onProgress?.(Math.min(1, e.loaded / e.total));
		};
		const onAbort = () => xhr.abort();
		signal?.addEventListener("abort", onAbort, { once: true });
		const done = () => signal?.removeEventListener("abort", onAbort);
		xhr.onload = () => {
			done();
			if (xhr.status >= 200 && xhr.status < 300) {
				onProgress?.(1);
				resolve();
			} else if (xhr.status >= 500) {
				reject(new ApiError("SERVICE_UNAVAILABLE", xhr.status, "The file store did not answer. Try again."));
			} else {
				reject(new ApiError("UPLOAD_INCOMPLETE", xhr.status, "The upload was refused. Try again."));
			}
		};
		xhr.onerror = () => {
			done();
			reject(new ApiError("SERVICE_UNAVAILABLE", 0, "Can't reach the file store right now."));
		};
		xhr.onabort = () => {
			done();
			reject(new DOMException("Aborted", "AbortError"));
		};
		xhr.send(file);
	});
}

/** True for the AbortError an aborted upload rejects with (not a failure to report). */
export const isAbort = (err: unknown): boolean => err instanceof DOMException && err.name === "AbortError";
